<template>
  <section id="hero" class="hero on-night">
    <!-- The cold open. At --p0: 0 this is the page: edge-to-edge night, no
         header rule, no white margin. The first 620px of scroll folds it into
         the CV's inset night card by scrubbing one custom property through
         margin, radius, height and shadow. Nothing is withheld and nothing is
         assembled — the warm ground is already painted behind it, so the
         inset reveals rather than builds. -->
    <div ref="masthead" class="masthead">
      <figure class="portrait">
        <div ref="portraitPlane" class="portrait-plane">
          <img
            class="portrait-img"
            :src="portraitSrc"
            :alt="$t('masthead.portraitAlt')"
            width="933"
            height="1400"
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
        </div>
      </figure>

      <div class="copy">
        <!-- One word carries the voice. Same size, same colour, same weight in
             optical terms — only the letterform changes, so this reads as a
             signature rather than as decoration. -->
        <!-- The separator is an interpolation, not source whitespace: Vue's
             whitespace: 'condense' deletes a whitespace-only text node that
             contains a newline, so reformatting these two spans onto separate
             lines would silently render — and announce — "BjörnKoemans". -->
        <h1 class="name">
          <span>{{ $t("masthead.nameGiven") }}</span
          >{{ " "
          }}<span class="name-em">{{ $t("masthead.nameFamily") }}</span>
        </h1>
        <p class="role">{{ $t("masthead.role") }}</p>

        <div class="readout">
          <!-- Atmosphere, not information: a screen reader announcing a
               changing time is noise. -->
          <span class="clock num" aria-hidden="true">
            <span class="clock-label">{{ $t("masthead.clockLabel") }}</span>
            <span class="clock-value">{{ clock }}</span>
          </span>
        </div>
      </div>

      <!-- Scroll affordance: a measurement rule with one tick descending it,
           in the page's own register. It inherits --p0 from .masthead, so it
           is gone by the time the fold is half done — and under reduced
           motion, where --p0 is stamped at 1, it never shows at all. -->
      <div class="cue" aria-hidden="true"><span class="cue-tick"></span></div>
    </div>
  </section>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { DEPTH, registerLayer } from "@/composables/useDepth";

export default {
  name: "MastheadSection",
  setup() {
    const masthead = ref(null);
    const portraitPlane = ref(null);
    const clock = ref("");

    let stopFold = null;
    let stopPortrait = null;
    let timer = null;

    // Built once. Constructing an Intl.DateTimeFormat is the expensive half
    // of this readout; formatting it is not.
    const time = new Intl.DateTimeFormat("nl-NL", {
      timeZone: "Europe/Amsterdam",
      hour: "2-digit",
      minute: "2-digit",
    });
    const tick = () => {
      clock.value = time.format(new Date());
    };
    tick(); // correct at first paint — the type never animates or fills in

    const portraitSrc = computed(() => {
      try {
        return require("@/assets/images/bjornkoemans.png");
      } catch (e) {
        return "";
      }
    });

    onMounted(() => {
      // k is LOCKED, not the registerLayer default of PLATE: the fold writes
      // --k on the masthead and every descendant inherits it, and 1.00 is the
      // truthful value — nothing inside this card is displaced by depth
      // except the portrait plane, which stamps its own --k.
      stopFold = registerLayer(masthead.value, { k: DEPTH.LOCKED, axis: "fold" });
      stopPortrait = registerLayer(portraitPlane.value, {
        k: DEPTH.NEAR,
        pointer: 10,
      });
      // The readout carries hours and minutes only, so 15s keeps it at most a
      // quarter-minute stale — on one interval, cleared on unmount.
      timer = setInterval(tick, 15000);
    });

    onBeforeUnmount(() => {
      if (timer) clearInterval(timer);
      if (stopFold) stopFold();
      if (stopPortrait) stopPortrait();
    });

    return { masthead, portraitPlane, clock, portraitSrc };
  },
};
</script>

<style scoped>
.hero {
  /* The warm set, scoped. App.vue still ships the cold tokens (--night:
     #0e0e11, --on-night-3: #86868b), so every var() below this section was
     still resolving blue-black while the literals in this file had already
     moved warm — the seam gradient was fading #0e0e11 into a transparent
     #14110e inside one gradient. These four lines are a bridge, not a home:
     when App.vue's palette is warmed, delete them. */
  --night: #14110e;
  --on-night: #f4f1ec;
  --on-night-2: #d6d0c6;
  --on-night-3: #8d857a; /* 5.2:1 on the night — same luminance as #86868b */

  position: relative;
  /* flow-root, not just relative: without a block formatting context the
     masthead's top margin collapses out through the section at --p0: 1,
     shifting the whole page down 24px and leaving 24px above / 48px below
     instead of an even inset. */
  display: flow-root;
  height: 100vh;
  min-height: 620px;
}

.masthead {
  position: relative;
  overflow: hidden;
  /* --p0 is written by useDepth on this element only (axis: 'fold').
     0 = full bleed, 1 = the CV's settled inset night card. */
  margin: calc(var(--p0, 0) * 24px);
  border-radius: calc(var(--p0, 0) * 28px);
  height: calc(100vh - var(--p0, 0) * 48px);
  /* Tracks the section's own min-height, so a short viewport (landscape
     phone, a 500px window) still opens on genuinely edge-to-edge night
     rather than a card floating on the warm ground. */
  min-height: calc(620px - var(--p0, 0) * 48px);
  /* The single chromatic event in the cold open: copper warmth banked under
     the copy, low enough that it reads as light in the room, not as a glow
     effect. Warm black everywhere else — on a warm ground a blue-black hero
     reads as a different page. */
  background: radial-gradient(
      60% 80% at 6% 112%,
      rgba(180, 82, 45, 0.15),
      transparent 70%
    ),
    var(--night);
  box-shadow: 0 calc(var(--p0, 0) * 10px) calc(var(--p0, 0) * 40px)
    rgba(20, 17, 14, 0.28);
}

/* =============================================================
   Portrait — the one depth layer in the masthead
   ============================================================= */
.portrait {
  position: absolute;
  inset: 0 0 0 auto;
  width: 42%;
  margin: 0;
  overflow: hidden;
}

.portrait-plane {
  position: absolute;
  /* Overscan: the plane travels ±15px on scroll and ±10px on pointer, and
     breathes 1.5%, so it must be larger than its frame or the night would
     show through an edge. */
  inset: -34px -18px;
  /* The photo's head sits high in the frame, and object-position cannot
     move it once the image already fills the box. So the plane itself
     starts a tenth of the way down: the night shows above it, which on a
     night ground is invisible, and the head lands where a face belongs. */
  top: 10%;
  --travel-y: calc(
    (var(--p, 0.5) - 0.5) * (1 - var(--k, 1)) * var(--travel) *
      var(--depth-scale)
  );
  transform: translate3d(var(--mx, 0px), var(--travel-y), 0) scale(var(--s, 1));
  /* Ambient breathing on the `scale` property rather than inside `transform`:
     an animation on transform would replace the depth/pointer rule outright.
     One element, one extra composited property, no new loop. */
  animation: portrait-breathe 14s var(--ease-inout) infinite;
}

@keyframes portrait-breathe {
  0%,
  100% {
    scale: 1;
  }
  50% {
    scale: 1.015;
  }
}

.portrait-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 26%;
}

/* The CV's seam. It sits on the frame, not on the moving plane, so the fade
   into the night stays locked to the card edge while the portrait drifts —
   which is what lets the shot bleed in with no cutout work. */
.portrait::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
      90deg,
      var(--night) 0%,
      var(--night) 10%,
      rgba(20, 17, 14, 0) 62%
    ),
    linear-gradient(0deg, rgba(20, 17, 14, 0.55) 0%, rgba(20, 17, 14, 0) 30%),
    linear-gradient(180deg, rgba(20, 17, 14, 0.4) 0%, rgba(20, 17, 14, 0) 26%);
}

/* =============================================================
   Copy — locked at k = 1.00, full opacity from frame one
   ============================================================= */
.copy {
  position: relative;
  z-index: 2;
  width: 58%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 0 0 0 clamp(24px, 6vw, 72px);
}

.kicker {
  font-family: var(--font-measure);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--on-night-3);
  margin-bottom: var(--s-5);
}

.name {
  font-size: var(--t-display);
  font-weight: 700;
  line-height: 1.02;
  letter-spacing: var(--ls-display);
  color: var(--on-night);
  margin-bottom: var(--s-4);
  text-wrap: balance;
}

.name-em {
  /* Named outright rather than through var(--font-display, …): that token is
     not defined anywhere in App.vue, so the fallback was doing all the work
     and the word was rendering in Georgia — with a synthesised 600, since
     Georgia has no semibold. Fraunces is now loaded in index.html. */
  font-family: "Fraunces", Georgia, "Times New Roman", serif;
  font-style: italic;
  /* 600 in Fraunces italic carries the same optical weight as 700 in Hanken
     Grotesk; matching the numbers would make the second word heavier. */
  font-weight: 600;
  font-optical-sizing: auto;
  /* The sans is tracked in at -0.032em. A serif italic at display size needs
     that air back or the ascenders collide. */
  letter-spacing: -0.012em;
}

.role {
  font-size: 20px;
  line-height: 1.4;
  color: var(--on-night-2);
  max-width: 42ch;
  text-wrap: pretty;
  margin-bottom: var(--s-3);
}

.statement {
  font-size: var(--t-body);
  line-height: 1.62;
  color: var(--on-night-3);
  max-width: 46ch;
  text-wrap: pretty;
}

/* =============================================================
   Live readout — the card's base line
   ============================================================= */
.readout {
  position: absolute;
  left: clamp(24px, 6vw, 72px);
  bottom: clamp(24px, 4vw, 44px);
  display: flex;
  align-items: center;
  gap: var(--s-5);
  font-size: var(--m-value);
}

.clock {
  display: inline-flex;
  align-items: baseline;
  gap: var(--s-2);
}
.clock-label {
  font-size: var(--m-label);
  letter-spacing: var(--ls-label);
  text-transform: uppercase;
  color: var(--on-night-3);
}
.clock-value {
  color: var(--on-night-2);
  font-weight: 500;
}

.status {
  display: inline-flex;
  align-items: center;
  gap: var(--s-2);
  font-size: var(--m-label);
  font-weight: 500;
  letter-spacing: var(--ls-label);
  text-transform: uppercase;
  color: var(--on-night-2);
}

/* The one animated shadow permitted on the page, on one 7px element.
   --amber is copper carried up to a tint that survives on the night; it is
   not a second hue. */
.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--amber);
  animation: status-pulse 2s var(--ease-inout) infinite;
}
@keyframes status-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(228, 176, 132, 0.45);
  }
  70% {
    box-shadow: 0 0 0 7px rgba(228, 176, 132, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(228, 176, 132, 0);
  }
}

/* =============================================================
   Scroll affordance — a rule and one descending tick
   ============================================================= */
.cue {
  position: absolute;
  left: 50%;
  bottom: clamp(24px, 4vw, 40px);
  z-index: 2;
  width: var(--hair-w);
  height: 56px;
  transform: translateX(-50%);
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(244, 241, 236, 0) 0%,
    rgba(244, 241, 236, 0.22) 34%,
    rgba(244, 241, 236, 0.04) 100%
  );
  /* Opacity is clamped to [0,1] by the engine, so the cue is fully gone at
     --p0 ≈ 0.42 — long before the fold settles. */
  opacity: calc(1 - var(--p0, 0) * 2.4);
}

.cue-tick {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 12px;
  background: var(--on-night-2);
  animation: cue-fall 3.6s var(--ease-inout) infinite;
}

@keyframes cue-fall {
  0% {
    transform: translateY(-12px);
    opacity: 0;
  }
  16% {
    opacity: 0.9;
  }
  58% {
    transform: translateY(46px);
    opacity: 0;
  }
  100% {
    transform: translateY(46px);
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dot {
    animation: none;
    box-shadow: 0 0 0 3px rgba(228, 176, 132, 0.16);
  }
  .portrait-plane {
    animation: none;
  }
  /* The cue is already invisible here (--p0 is stamped at 1), but the tick
     must not be left animating behind it. */
  .cue-tick {
    animation: none;
    transform: translateY(20px);
    opacity: 0.7;
  }
}

/* =============================================================
   Mobile — portrait hidden, readout back into the flow
   ============================================================= */
@media (max-width: 720px) {
  .masthead {
    margin: calc(var(--p0, 0) * 12px);
    border-radius: calc(var(--p0, 0) * 22px);
    height: calc(100vh - var(--p0, 0) * 24px);
    min-height: calc(620px - var(--p0, 0) * 24px);
  }

  .portrait {
    display: none;
  }

  .copy {
    width: 100%;
    padding: 0 var(--edge);
  }

  .name {
    font-size: clamp(34px, 9vw, 44px);
  }

  .role {
    font-size: var(--t-lead);
  }

  .readout {
    position: static;
    margin-top: var(--s-6);
    flex-direction: column;
    align-items: flex-start;
    gap: var(--s-3);
  }

  /* Touch scrolls without being asked, and the readout is in the flow here —
     the cue would land on top of it. */
  .cue {
    display: none;
  }
}
</style>
